import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { useAuth } from '../auth/AuthProvider'
import { useActiveVenue } from '../venues/ActiveVenueProvider'
import type { RestaurantCatalogOfferDto } from '../api/restaurantCatalog'
import { demoCartLines } from '../dev/buyerDemoData'
import { isDemoUiEnabled } from '../dev/demoMode'
import { cartLineKey } from './cartLineKey'
import { clearCartLines, loadCartLines, saveCartLines } from './storage'
import type { CartLine } from './types'

type AddToCartInput = {
  offer: RestaurantCatalogOfferDto
  productTypeName: string
  quantity?: number
}

type CartContextValue = {
  lines: CartLine[]
  lineCount: number
  addOffer: (input: AddToCartInput) => void
  updateQuantity: (key: string, quantity: number) => void
  updateNote: (key: string, note: string) => void
  removeLine: (key: string) => void
  removeLinesByPrivateSupplier: (privateSupplierId: number) => void
  /** Replace draft lines for a private supplier (reuse saved blank). */
  restoreExternalSupplierLines: (input: {
    privateSupplierId: number
    supplierName: string
    lines: Array<{
      catalogItemId: number
      itemName: string
      brand?: string | null
      unit: string
      price?: string | null
      quantity: number
      note?: string
      productTypeId?: number
      productTypeName?: string
    }>
  }) => void
  clearCart: () => void
  hasLine: (key: string) => boolean
}

const CartContext = createContext<CartContextValue | null>(null)

const offerToCartLine = (input: AddToCartInput): CartLine => {
  if (input.offer.productTypeId == null) {
    throw new Error('Cannot add catalog offer without productTypeId to cart')
  }
  return {
  catalogItemId: input.offer.itemId,
  supplierKind: input.offer.isRegisteredSupplier ? 'registered' : 'external',
  supplierOrganizationId: input.offer.supplierOrganizationId,
  privateSupplierId: input.offer.privateSupplierId,
  supplierOrganizationName: input.offer.supplierOrganizationName,
  productTypeId: input.offer.productTypeId,
  productTypeName: input.productTypeName,
  itemName: input.offer.itemName,
  brand: input.offer.brand,
  // Keep the raw catalog unit (not the localized display label from
  // formatUnitLabel) - RfqCreateScreen sends this verbatim to the backend,
  // which matches it byte-for-byte (case-insensitively) against the supplier
  // catalog item's own unit string. Formatting it here (e.g. "kg" -> "кг")
  // made every RFQ line for a Latin-script catalog unit fail with
  // "Единица измерения в заявке не совпадает с каталогом поставщика".
  // Display call sites apply formatUnitLabel themselves when rendering.
  unit: input.offer.unit?.trim() || 'кг',
  price: input.offer.price ?? null,
  quantity: input.quantity ?? 1,
  note: '',
}
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { session, role } = useAuth()
  const { activeVenueId } = useActiveVenue()
  const organizationId = session?.organizationId ?? null
  const [lines, setLines] = useState<CartLine[]>([])

  useEffect(() => {
    if (role !== 'buyer' || organizationId == null) {
      setLines([])
      return
    }
    let loaded = loadCartLines(organizationId, activeVenueId)
    if (isDemoUiEnabled && loaded.length === 0) {
      loaded = demoCartLines
      saveCartLines(organizationId, loaded, activeVenueId)
    }
    setLines(loaded)
  }, [activeVenueId, organizationId, role])

  const addOffer = useCallback(
    (input: AddToCartInput) => {
      const incoming = offerToCartLine(input)
      const key = cartLineKey(incoming)
      setLines((current) => {
        const existingIndex = current.findIndex((line) => cartLineKey(line) === key)
        let next: CartLine[]
        if (existingIndex >= 0) {
          next = [...current]
          const existing = next[existingIndex]
          next[existingIndex] = {
            ...existing,
            quantity: existing.quantity + (input.quantity ?? 1),
            price: incoming.price,
            productTypeName: incoming.productTypeName,
          }
        } else {
          next = [...current, incoming]
        }
        if (organizationId != null) {
          saveCartLines(organizationId, next, activeVenueId)
        }
        return next
      })
    },
    [activeVenueId, organizationId],
  )

  const updateQuantity = useCallback(
    (key: string, quantity: number) => {
      if (!Number.isFinite(quantity) || quantity <= 0) {
        return
      }
      setLines((current) => {
        const next = current.map((line) => (cartLineKey(line) === key ? { ...line, quantity } : line))
        if (organizationId != null) {
          saveCartLines(organizationId, next, activeVenueId)
        }
        return next
      })
    },
    [activeVenueId, organizationId],
  )

  const updateNote = useCallback(
    (key: string, note: string) => {
      setLines((current) => {
        const next = current.map((line) => (cartLineKey(line) === key ? { ...line, note } : line))
        if (organizationId != null) {
          saveCartLines(organizationId, next, activeVenueId)
        }
        return next
      })
    },
    [activeVenueId, organizationId],
  )

  const removeLine = useCallback(
    (key: string) => {
      setLines((current) => {
        const next = current.filter((line) => cartLineKey(line) !== key)
        if (organizationId != null) {
          saveCartLines(organizationId, next, activeVenueId)
        }
        return next
      })
    },
    [activeVenueId, organizationId],
  )

  const removeLinesByPrivateSupplier = useCallback(
    (privateSupplierId: number) => {
      setLines((current) => {
        const next = current.filter(
          (line) =>
            !(line.supplierKind === 'external' && line.privateSupplierId === privateSupplierId),
        )
        if (organizationId != null) {
          saveCartLines(organizationId, next, activeVenueId)
        }
        return next
      })
    },
    [activeVenueId, organizationId],
  )

  const restoreExternalSupplierLines = useCallback(
    (input: {
      privateSupplierId: number
      supplierName: string
      lines: Array<{
        catalogItemId: number
        itemName: string
        brand?: string | null
        unit: string
        price?: string | null
        quantity: number
        note?: string
        productTypeId?: number
        productTypeName?: string
      }>
    }) => {
      setLines((current) => {
        const withoutSupplier = current.filter(
          (line) =>
            !(
              line.supplierKind === 'external' &&
              line.privateSupplierId === input.privateSupplierId
            ),
        )
        const restored: CartLine[] = input.lines
          .filter((line) => Number.isFinite(line.catalogItemId) && line.catalogItemId > 0)
          .map((line) => ({
            catalogItemId: line.catalogItemId,
            supplierKind: 'external' as const,
            supplierOrganizationId: null,
            privateSupplierId: input.privateSupplierId,
            supplierOrganizationName: input.supplierName,
            productTypeId: line.productTypeId ?? 0,
            productTypeName: line.productTypeName?.trim() || 'Без категории',
            itemName: line.itemName,
            brand: line.brand ?? null,
            unit: line.unit.trim() || 'кг',
            price: line.price ?? null,
            quantity: line.quantity > 0 ? line.quantity : 1,
            note: line.note?.trim() ?? '',
          }))
        const next = [...withoutSupplier, ...restored]
        if (organizationId != null) {
          saveCartLines(organizationId, next, activeVenueId)
        }
        return next
      })
    },
    [activeVenueId, organizationId],
  )

  const clearCart = useCallback(() => {
    if (organizationId != null) {
      clearCartLines(organizationId, activeVenueId)
    }
    setLines([])
  }, [activeVenueId, organizationId])

  const hasLine = useCallback(
    (key: string) => lines.some((line) => cartLineKey(line) === key),
    [lines],
  )

  const value = useMemo<CartContextValue>(
    () => ({
      lines,
      lineCount: lines.length,
      addOffer,
      updateQuantity,
      updateNote,
      removeLine,
      removeLinesByPrivateSupplier,
      restoreExternalSupplierLines,
      clearCart,
      hasLine,
    }),
    [
      lines,
      addOffer,
      updateQuantity,
      updateNote,
      removeLine,
      removeLinesByPrivateSupplier,
      restoreExternalSupplierLines,
      clearCart,
      hasLine,
    ],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = (): CartContextValue => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within CartProvider')
  }
  return context
}
